import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { authApi, type VerifyResponse } from '@/api/auth';
import { Brand } from '@/components/Brand';
import { CasinoButton } from '@/components/CasinoButton';
import { Flaming7 } from '@/components/Flaming7';
import { useSession } from '@/state/session';

type Step = 'email' | 'code' | 'admin' | 'profile';

/**
 * Multi-step login state survives a page reload / iOS Safari background-tab
 * eviction via sessionStorage. Without this, switching to Mail to grab the
 * 6-digit code and switching back to Safari resets the React tree, dropping
 * the user from the "enter code" step back to "enter email".
 *
 * Persisted: step, email, codeSentAt. Intentionally NOT persisted: the
 * 6-digit code itself, pending/error/transient UI, password / displayName /
 * phone (post-verify steps).
 */
const LOGIN_STATE_KEY = 'parks-slots-login-state';
// Notice flag uses a SEPARATE key so the main-state cleanup never erases
// it mid-React-lifecycle. React StrictMode dev unmounts and remounts on
// first mount, and if the notice were derived from the main state, the
// remount would always see 'none' (because mount 1's effect just cleared
// it) and the notice would silently fail to render in dev.
const LOGIN_EXPIRY_NOTICE_KEY = 'parks-slots-login-expiry-notice';
const CODE_TTL_MS = 10 * 60 * 1000; // matches server-side expires_at

type PersistedLoginState = {
  step: Extract<Step, 'email' | 'code'>;
  email: string;
  codeSentAt: number;
};

type ReadResult =
  | { kind: 'fresh'; state: PersistedLoginState }
  | { kind: 'expired' }
  | { kind: 'none' };

/**
 * Pure read — no side effects. Safe to invoke twice (React StrictMode
 * double-runs lazy state initializers in dev). If the persisted record
 * is malformed or expired, the CALLER must remove it; doing so here
 * would make the second strict-mode invocation see 'none' instead of
 * the real kind, and React would commit the wrong value.
 */
function readLoginState(): ReadResult {
  try {
    const raw = sessionStorage.getItem(LOGIN_STATE_KEY);
    if (!raw) return { kind: 'none' };
    const parsed = JSON.parse(raw) as unknown;
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      typeof (parsed as Record<string, unknown>).step !== 'string' ||
      typeof (parsed as Record<string, unknown>).email !== 'string' ||
      typeof (parsed as Record<string, unknown>).codeSentAt !== 'number'
    ) {
      return { kind: 'none' };
    }
    const s = parsed as PersistedLoginState;
    if (s.step !== 'email' && s.step !== 'code') {
      return { kind: 'none' };
    }
    if (s.step === 'code' && Date.now() - s.codeSentAt > CODE_TTL_MS) {
      return { kind: 'expired' };
    }
    return { kind: 'fresh', state: s };
  } catch {
    // sessionStorage can throw under quota / private mode / disabled storage.
    return { kind: 'none' };
  }
}

function writeLoginState(s: PersistedLoginState): void {
  try {
    sessionStorage.setItem(LOGIN_STATE_KEY, JSON.stringify(s));
  } catch {
    // best-effort; if storage fails the flow still works in-memory
  }
}

function clearLoginState(): void {
  try {
    sessionStorage.removeItem(LOGIN_STATE_KEY);
  } catch {
    /* ignore */
  }
}

function readExpiryNotice(): boolean {
  try {
    return sessionStorage.getItem(LOGIN_EXPIRY_NOTICE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeExpiryNotice(): void {
  try {
    sessionStorage.setItem(LOGIN_EXPIRY_NOTICE_KEY, '1');
  } catch {
    /* ignore */
  }
}

function clearExpiryNotice(): void {
  try {
    sessionStorage.removeItem(LOGIN_EXPIRY_NOTICE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * "Terminal" classifier for verify-code errors. Wrong-code-but-retryable
 * mistypes are NOT terminal (we want to keep the persisted state so the
 * user can retry). Expired codes, rate-limit lockout, and code-already-used
 * ARE terminal — wipe the flow so the next page load starts fresh.
 *
 * Server returns 401 invalid_code for both "wrong" and "expired" — we
 * disambiguate using the local codeSentAt timestamp.
 */
function isTerminalVerifyError(
  err: unknown,
  codeSentAt: number,
): boolean {
  const e = err as { status?: number; error?: string };
  if (e?.status === 429) return true; // rate-limited — flow is locked out
  if (e?.error === 'unauthenticated') return true;
  if (e?.error === 'invalid_code') {
    // Server can't tell us "expired" vs "wrong"; use the local timestamp.
    return Date.now() - codeSentAt > CODE_TTL_MS;
  }
  return false;
}

export function Login() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setUser = useSession((s) => s.setUser);

  // Hydrate from sessionStorage exactly once at mount. The lazy initializer
  // pattern ensures readLoginState() runs in the SAME tick that useState
  // seeds — no flash of the email-step before we land on the code-step.
  // (readLoginState is pure; clearing the persisted record after a
  // malformed or expired hydration happens in the useEffect below so
  // StrictMode's double-invocation doesn't lose the value.)
  const hydrated = useState(readLoginState)[0];
  const initial: PersistedLoginState =
    hydrated.kind === 'fresh'
      ? hydrated.state
      : { step: 'email', email: '', codeSentAt: 0 };

  const [step, setStep] = useState<Step>(initial.step);
  const [email, setEmail] = useState(initial.email);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [phone, setPhone] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeSentAt, setCodeSentAt] = useState<number>(initial.codeSentAt);
  const [resendIn, setResendIn] = useState(0);
  const [resendInfo, setResendInfo] = useState<string | null>(null);
  // Show "code expired" once if the hydrated record was past its TTL.
  // Stored on a separate sessionStorage key so React StrictMode's
  // unmount/remount cycle doesn't lose the flag between mounts.
  // Self-clears the first time the user types in the email field.
  const [expiredNotice, setExpiredNotice] = useState(
    () => hydrated.kind === 'expired' || readExpiryNotice(),
  );

  // If hydration found an expired or malformed record, evict the main
  // state record and persist a separate notice flag so the notice
  // survives StrictMode's mount/unmount/remount in dev.
  useEffect(() => {
    if (hydrated.kind === 'expired') writeExpiryNotice();
    if (hydrated.kind !== 'fresh') clearLoginState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist {step, email, codeSentAt} whenever any of those change.
  // Skips the admin/profile post-verify steps — those are post-auth and
  // shouldn't be resumable via sessionStorage. Also skips the "fully
  // blank" default state so a fresh /login visit doesn't leave a useless
  // sessionStorage row behind.
  useEffect(() => {
    if (step !== 'email' && step !== 'code') return;
    if (step === 'email' && email === '' && codeSentAt === 0) {
      clearLoginState();
      return;
    }
    writeLoginState({ step, email, codeSentAt });
  }, [step, email, codeSentAt]);

  // Drive the resend cooldown countdown.
  useEffect(() => {
    if (step !== 'code' || codeSentAt === 0) {
      setResendIn(0);
      return;
    }
    const tick = () => {
      const elapsed = (Date.now() - codeSentAt) / 1000;
      setResendIn(Math.max(0, Math.ceil(30 - elapsed)));
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [step, codeSentAt]);

  async function submitEmail(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await authApi.requestCode(email.trim());
      setCodeSentAt(Date.now());
      setStep('code');
      // Sending a new code clears any leftover expiry notice.
      if (expiredNotice) {
        setExpiredNotice(false);
        clearExpiryNotice();
      }
    } catch (err) {
      setError(humanError(err, 'Could not send code.'));
    } finally {
      setPending(false);
    }
  }

  async function resendCode() {
    if (resendIn > 0 || pending) return;
    setError(null);
    setResendInfo(null);
    setPending(true);
    try {
      // Server-side `issueCode` invalidates any prior outstanding code,
      // so the previous one is rendered useless as soon as this resolves.
      await authApi.requestCode(email.trim());
      setCodeSentAt(Date.now());
      setCode('');
      setResendInfo('New code sent. Check your email.');
    } catch (err) {
      setError(humanError(err, 'Could not resend code.'));
    } finally {
      setPending(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const res = await authApi.verifyCode(email.trim(), code.trim());
      await afterVerify(res);
    } catch (err) {
      const msg = (err as { error?: string }).error;
      setError(msg === 'invalid_code' ? 'Code is wrong or expired.' : humanError(err));
      // Terminal verify failures (expired code, rate-limit lockout) wipe
      // the persisted flow so the next page load starts at the email
      // step instead of stranding the user on a dead code form. The
      // notice flag survives reload via its own storage key.
      if (isTerminalVerifyError(err, codeSentAt)) {
        clearLoginState();
        writeExpiryNotice();
        setStep('email');
        setCode('');
        setCodeSentAt(0);
        setExpiredNotice(true);
      }
    } finally {
      setPending(false);
    }
  }

  async function afterVerify(res: VerifyResponse) {
    setUser(res.user);
    // Hydrate the cache directly. invalidateQueries alone marks stale but
    // leaves the old value (possibly {user:null} from an earlier Home visit),
    // which would race with Me's redirect guard.
    qc.setQueryData(['me'], { user: res.user });

    if (res.needsAdminPassword) {
      // Email-step verified — wipe sessionStorage now. The admin/profile
      // steps aren't part of the persisted flow (they shouldn't survive
      // a reload — server already issued a session cookie).
      clearLoginState();
      clearExpiryNotice();
      setStep('admin');
      return;
    }
    if (res.needsProfile) {
      clearLoginState();
      clearExpiryNotice();
      setStep('profile');
      return;
    }
    clearLoginState();
    clearExpiryNotice();
    navigate('/me');
  }

  async function submitAdminPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await authApi.adminPassword(password);
      const { user } = await authApi.me();
      setUser(user);
      qc.setQueryData(['me'], { user });
      navigate('/me');
    } catch (err) {
      setError(humanError(err, 'Wrong password.'));
    } finally {
      setPending(false);
    }
  }

  async function submitProfile(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      const { user } = await authApi.updateProfile({
        displayName: displayName.trim(),
        phone: phone.trim() || undefined,
      });
      setUser(user);
      qc.setQueryData(['me'], { user });
      navigate('/me');
    } catch (err) {
      setError(humanError(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center px-5 py-10">
      <div className="mb-8 flex flex-col items-center gap-3">
        <Flaming7 size={130} />
        <Brand size="md" />
      </div>

      <div className="cabinet-card w-full p-6">
        {step === 'email' && (
          <form onSubmit={submitEmail} className="space-y-4">
            <header>
              <h2 className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
                Sign in
              </h2>
              <p className="text-sm text-cabinet-cream/70 mt-1">
                Enter your email — we'll send a 6-digit code.
              </p>
            </header>
            <Field label="Email">
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                autoFocus
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (expiredNotice) {
                    setExpiredNotice(false);
                    clearExpiryNotice();
                  }
                }}
                className={inputCls}
              />
            </Field>
            <CasinoButton fullWidth type="submit" disabled={pending}>
              {pending ? 'Sending…' : 'Send code'}
            </CasinoButton>
            {expiredNotice && !error && (
              <p className="text-xs text-cabinet-flame-orange">
                Your previous code expired — please request a new one.
              </p>
            )}
            {error && <ErrorBlock>{error}</ErrorBlock>}
          </form>
        )}

        {step === 'code' && (
          <form onSubmit={submitCode} className="space-y-4">
            <header>
              <h2 className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
                Enter code
              </h2>
              <p className="text-sm text-cabinet-cream/70 mt-1">
                Check {email} for a 6-digit code. Expires in 10 minutes.
              </p>
            </header>
            <Field label="Code">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={`${inputCls} font-led tracking-[0.3em] text-center text-2xl`}
              />
            </Field>
            <CasinoButton fullWidth type="submit" disabled={pending || code.length !== 6}>
              {pending ? 'Checking…' : 'Verify'}
            </CasinoButton>
            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                className="text-cabinet-cream/60 hover:text-cabinet-gold"
                onClick={() => {
                  setCode('');
                  setError(null);
                  setResendInfo(null);
                  setCodeSentAt(0);
                  setEmail('');
                  setStep('email');
                  setExpiredNotice(false);
                  clearLoginState();
                  clearExpiryNotice();
                }}
              >
                ← Use a different email
              </button>
              <button
                type="button"
                onClick={resendCode}
                disabled={resendIn > 0 || pending}
                className="text-cabinet-gold hover:text-cabinet-gold-bright disabled:text-cabinet-cream/30 disabled:cursor-not-allowed"
              >
                {resendIn > 0 ? `Resend in ${resendIn}s` : 'Resend code'}
              </button>
            </div>
            {resendInfo && (
              <p className="text-xs text-cabinet-gold-bright">{resendInfo}</p>
            )}
            {error && <ErrorBlock>{error}</ErrorBlock>}
          </form>
        )}

        {step === 'admin' && (
          <form onSubmit={submitAdminPassword} className="space-y-4">
            <header>
              <h2 className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
                Admin password
              </h2>
              <p className="text-sm text-cabinet-cream/70 mt-1">
                Your account requires an admin password to unlock admin tools.
              </p>
            </header>
            <Field label="Password">
              <input
                type="password"
                autoComplete="current-password"
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </Field>
            <CasinoButton fullWidth variant="red" type="submit" disabled={pending}>
              {pending ? 'Verifying…' : 'Unlock admin'}
            </CasinoButton>
            <button
              type="button"
              className="block w-full text-center text-sm text-cabinet-cream/60 hover:text-cabinet-gold"
              onClick={() => navigate('/me')}
            >
              Skip — continue as player
            </button>
            {error && <ErrorBlock>{error}</ErrorBlock>}
          </form>
        )}

        {step === 'profile' && (
          <form onSubmit={submitProfile} className="space-y-4">
            <header>
              <h2 className="font-display text-cabinet-gold text-xl uppercase tracking-widest">
                Complete profile
              </h2>
              <p className="text-sm text-cabinet-cream/70 mt-1">
                What should leaderboards call you?
              </p>
            </header>
            <Field label="Display name">
              <input
                type="text"
                required
                autoFocus
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className={inputCls}
              />
            </Field>
            <Field label="Phone (optional)">
              <input
                type="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputCls}
              />
            </Field>
            <CasinoButton fullWidth type="submit" disabled={pending || !displayName.trim()}>
              {pending ? 'Saving…' : 'Continue'}
            </CasinoButton>
            {error && <ErrorBlock>{error}</ErrorBlock>}
          </form>
        )}
      </div>
    </main>
  );
}

const inputCls =
  'w-full rounded-md bg-black/50 border-2 border-cabinet-cream/20 px-3 py-2.5 text-cabinet-cream placeholder:text-cabinet-cream/40 focus:border-cabinet-gold focus:outline-none focus:ring-2 focus:ring-cabinet-gold/40 transition';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-widest text-cabinet-cream/60 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function ErrorBlock({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md bg-cabinet-flame-red/20 border border-cabinet-flame-red px-3 py-2 text-sm text-cabinet-cream">
      {children}
    </div>
  );
}

function humanError(err: unknown, fallback = 'Something went wrong.'): string {
  const e = err as { error?: string };
  switch (e?.error) {
    case 'invalid_input':
      return 'Check your input — something is invalid.';
    case 'invalid_code':
      return 'Code is wrong or expired.';
    case 'unauthenticated':
      return 'Session expired. Please start over.';
    case 'bad_password':
      return 'Wrong password.';
    case 'not_admin':
      return "You're not an admin.";
    default:
      return fallback;
  }
}
